import { inject } from "@angular/core";
import { HttpEvent, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { Observable, catchError, filter, first, from, map, race, switchMap, tap, throwError } from "rxjs";
import { OFFLINE_RESOURCE, type OfflineResource } from "@core/consts/offline-resource.constants";
import { ConnectivityService } from "@shared/services/connectivity/connectivity.service";
import { OfflineResponsesService } from "@shared/services/offline/offline-responses.service";
import { isNetworkHttpError } from "@shared/helpers/http-error-predicates.helper";

export const offlineInterceptor: HttpInterceptorFn = (request, next) => {
  const resource: OfflineResource | null = request.context.get(OFFLINE_RESOURCE);
  if (!resource) return next(request);

  const connectivity: ConnectivityService = inject(ConnectivityService);
  const offlineResponses: OfflineResponsesService = inject(OfflineResponsesService);

  const local: () => Observable<HttpEvent<unknown>> = (): Observable<HttpEvent<unknown>> =>
    from(offlineResponses.respond(resource, request.params)).pipe(
      map((body: unknown) => new HttpResponse({ status: 200, url: request.urlWithParams, body })),
    );

  if (!connectivity.isConnected()) return local();

  const wentOffline: Observable<HttpEvent<unknown>> = connectivity.status$.pipe(
    filter(() => !connectivity.isConnected()),
    first(),
    switchMap(local),
  );

  const network: Observable<HttpEvent<unknown>> = next(request).pipe(
    filter((event: HttpEvent<unknown>): event is HttpResponse<unknown> => event instanceof HttpResponse),
    tap((response: HttpResponse<unknown>) => void offlineResponses.refresh(resource, response.body)),
    catchError((error: unknown) => (isNetworkHttpError(error) ? local() : throwError(() => error))),
  );

  return race(network, wentOffline);
};
